import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useSelector } from 'react-redux';
import { enrollmentService, EnrollmentWithProduct } from '@/services/enrollment.service';
import { bundleService, Bundle } from '@/services/bundle.service';
import { courseService, Course } from '@/services/course.service';
import { Package, X, BookOpen, Clock, TrendingUp } from 'lucide-react';
import { trackDashboard, trackPageView, trackModal } from '@/services/analytics';
import { useScrollTracking } from '@/hooks/useAnalytics';

const DashboardBundles: React.FC = () => {
  const navigate = useNavigate();
  const user = useSelector((state: any) => state.user.user);
  const [enrollments, setEnrollments] = useState<EnrollmentWithProduct[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [showModal, setShowModal] = useState(false);
  const [selectedBundle, setSelectedBundle] = useState<Bundle | null>(null);
  const [bundleCourses, setBundleCourses] = useState<Course[]>([]);
  const [loadingModal, setLoadingModal] = useState(false);

  useScrollTracking('DashboardBundles');

  useEffect(() => {
    trackDashboard.view('bundles');
    trackPageView('DashboardBundles');
  }, []);

  useEffect(() => {
    const fetchEnrollments = async () => {
      const token = localStorage.getItem('access_token');

      if (!token) {
        navigate('/signin');
        return;
      }

      try {
        setLoading(true);
        const response = await enrollmentService.getMyEnrollments();

        if (response.success && response.data) {
          // Filter to only show BUNDLE enrollments (exclude courses)
          const bundleEnrollments = response.data.filter(
            (enrollment) => enrollment.product_type === 'bundle'
          );
          setEnrollments(bundleEnrollments);
        } else {
          setError(response.error || 'Failed to load your bundles');
        }
      } catch (err) {
        console.error('Error fetching enrollments:', err);
        setError('An error occurred while loading your bundles');
      } finally {
        setLoading(false);
      }
    };

    fetchEnrollments();
  }, [navigate]);

  const handleBundleClick = async (enrollment: EnrollmentWithProduct) => {
    const bundleId = enrollment.product_id;
    if (!bundleId) {
      console.error('No bundle ID found for enrollment:', enrollment);
      setError('Unable to access this bundle. Please contact support.');
      return;
    }

    try {
      setLoadingModal(true);
      setShowModal(true);

      // Fetch bundle details
      const bundleResponse = await bundleService.getBundleById(bundleId);
      if (!bundleResponse.success || !bundleResponse.data) {
        setError('Failed to load bundle details');
        setShowModal(false);
        return;
      }

      setSelectedBundle(bundleResponse.data);

      // Fetch all courses in the bundle
      const coursePromises = bundleResponse.data.course_ids.map(courseId =>
        courseService.getCourseById(courseId)
      );
      const courseResponses = await Promise.all(coursePromises);
      const courses = courseResponses
        .filter(response => response.success && response.data)
        .map(response => response.data!);

      setBundleCourses(courses);
    } catch (err) {
      console.error('Error loading bundle details:', err);
      setError('Failed to load bundle details');
      setShowModal(false);
    } finally {
      setLoadingModal(false);
    }
  };

  const formatDate = (dateString: string) => {
    const date = new Date(dateString);
    return date.toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'long',
      day: 'numeric'
    });
  };

  const getProgressColor = (progress: number) => {
    if (progress === 0) return 'bg-gray-500';
    if (progress < 50) return 'bg-yellow-500';
    if (progress < 100) return 'bg-blue-500';
    return 'bg-green-500';
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-[60vh]">
        <div className="text-center">
          <div className="w-16 h-16 border-4 border-indigo-500 border-t-transparent rounded-full animate-spin mx-auto mb-4"></div>
          <p className="text-gray-300 text-lg">Loading your bundles...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="max-w-7xl mx-auto">
      {/* Header */}
      <div className="mb-8">
        <h1 className="text-4xl font-bold text-white mb-2 flex items-center gap-3">
          <Package className="w-10 h-10" />
          My Bundles
        </h1>
        <p className="text-gray-300 text-lg">
          Access your course bundles and bundled content
        </p>
      </div>

      {/* Error Message */}
      {error && (
        <div className="mb-6 p-4 bg-red-900/50 border border-red-500 rounded-lg">
          <p className="text-red-200">{error}</p>
        </div>
      )}

      {/* Enrollments Grid */}
      {enrollments.length === 0 ? (
        <div className="bg-slate-800/60 backdrop-blur-sm border-2 border-indigo-500/30 rounded-2xl p-12 text-center">
          <Package className="w-24 h-24 mx-auto mb-4 text-indigo-400" />
          <h3 className="text-2xl font-semibold text-gray-300 mb-2">No bundles yet</h3>
          <p className="text-gray-400 mb-6">
            Save money by enrolling in our curated course bundles
          </p>
          <button
            onClick={() => navigate('/bundles')}
            className="px-6 py-3 bg-gradient-to-r from-indigo-600 to-purple-600 text-white rounded-lg hover:shadow-lg hover:shadow-indigo-500/50 transition-all"
          >
            Browse Bundles
          </button>
        </div>
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
          {enrollments.map((enrollment) => (
            <div
              key={enrollment.id}
              className="bg-slate-800/60 backdrop-blur-sm border-2 border-indigo-500/30 rounded-xl overflow-hidden hover:border-indigo-500 transition-all duration-300 hover:-translate-y-1 hover:shadow-xl hover:shadow-indigo-500/30 cursor-pointer"
              onClick={() => handleBundleClick(enrollment)}
            >
              {/* Thumbnail */}
              <div className="h-48 bg-gradient-to-br from-purple-600 to-pink-600 relative overflow-hidden">
                {enrollment.product_details?.thumbnail_url ? (
                  <img
                    src={enrollment.product_details.thumbnail_url}
                    alt={enrollment.product_title}
                    className="w-full h-full object-cover"
                  />
                ) : (
                  <div className="w-full h-full flex items-center justify-center">
                    <Package className="w-20 h-20 text-white/50" />
                  </div>
                )}

                {/* Bundle Badge */}
                <div className="absolute top-3 left-3 bg-purple-500 text-white px-3 py-1 rounded-full text-xs font-semibold flex items-center gap-1">
                  <Package className="w-3 h-3" />
                  Bundle
                </div>

                {/* Certificate Badge */}
                {enrollment.certificate_issued && (
                  <div className="absolute top-3 right-3 bg-green-500 text-white px-3 py-1 rounded-full text-xs font-semibold flex items-center gap-1">
                    <svg className="w-4 h-4" fill="currentColor" viewBox="0 0 20 20">
                      <path d="M9 2a1 1 0 000 2h2a1 1 0 100-2H9z" />
                      <path fillRule="evenodd" d="M4 5a2 2 0 012-2 3 3 0 003 3h2a3 3 0 003-3 2 2 0 012 2v11a2 2 0 01-2 2H6a2 2 0 01-2-2V5zm9.707 5.707a1 1 0 00-1.414-1.414L9 12.586l-1.293-1.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z" clipRule="evenodd" />
                    </svg>
                    Certificate
                  </div>
                )}
              </div>

              {/* Content */}
              <div className="p-6">
                <h3 className="text-xl font-bold text-white mb-2 line-clamp-2">
                  {enrollment.product_title}
                </h3>

                {enrollment.product_details?.description && (
                  <p className="text-gray-400 text-sm mb-4 line-clamp-2">
                    {enrollment.product_details.description}
                  </p>
                )}

                {/* Progress Bar */}
                <div className="mb-4">
                  <div className="flex justify-between items-center mb-1">
                    <span className="text-xs text-gray-400">Overall Progress</span>
                    <span className="text-xs font-semibold text-gray-300">
                      {enrollment.progress_percentage.toFixed(0)}%
                    </span>
                  </div>
                  <div className="w-full bg-gray-700 rounded-full h-2">
                    <div
                      className={`h-2 rounded-full transition-all duration-300 ${getProgressColor(enrollment.progress_percentage)}`}
                      style={{ width: `${enrollment.progress_percentage}%` }}
                    ></div>
                  </div>
                </div>

                {/* Footer Info */}
                <div className="flex justify-between items-center text-xs text-gray-500 pt-4 border-t border-gray-700">
                  <span>Enrolled {formatDate(enrollment.enrolled_at)}</span>
                  <span className="capitalize flex items-center gap-1">
                    <Package className="w-3 h-3" />
                    {enrollment.product_type}
                  </span>
                </div>
              </div>
            </div>
          ))}
        </div>
      )}

      {/* Bundle Details Modal */}
      {showModal && (
        <div
          className="fixed inset-0 bg-black/70 backdrop-blur-sm z-50 flex items-center justify-center p-4"
          onClick={() => setShowModal(false)}
        >
          <div
            className="bg-slate-900 border-2 border-purple-500/50 rounded-2xl max-w-4xl w-full max-h-[90vh] overflow-hidden shadow-2xl shadow-purple-500/20"
            onClick={(e) => e.stopPropagation()}
          >
            {loadingModal ? (
              <div className="flex items-center justify-center p-12">
                <div className="text-center">
                  <div className="w-16 h-16 border-4 border-purple-500 border-t-transparent rounded-full animate-spin mx-auto mb-4"></div>
                  <p className="text-gray-300 text-lg">Loading bundle details...</p>
                </div>
              </div>
            ) : selectedBundle ? (
              <>
                {/* Modal Header with Thumbnail */}
                <div className="relative h-64 bg-gradient-to-br from-purple-600 to-pink-600 overflow-hidden">
                  {selectedBundle.thumbnail_url ? (
                    <img
                      src={selectedBundle.thumbnail_url}
                      alt={selectedBundle.title}
                      className="w-full h-full object-cover"
                    />
                  ) : (
                    <div className="w-full h-full flex items-center justify-center">
                      <Package className="w-32 h-32 text-white/30" />
                    </div>
                  )}
                  <div className="absolute inset-0 bg-gradient-to-t from-slate-900 via-slate-900/50 to-transparent"></div>

                  {/* Close Button */}
                  <button
                    onClick={() => setShowModal(false)}
                    className="absolute top-4 right-4 p-2 bg-slate-900/80 hover:bg-slate-800 text-white rounded-full transition-all hover:scale-110"
                  >
                    <X className="w-6 h-6" />
                  </button>

                  {/* Bundle Title Overlay */}
                  <div className="absolute bottom-0 left-0 right-0 p-6">
                    <div className="flex items-center gap-2 mb-2">
                      <Package className="w-6 h-6 text-purple-400" />
                      <span className="text-purple-400 font-semibold">Bundle</span>
                    </div>
                    <h2 className="text-4xl font-bold text-white mb-2">{selectedBundle.title}</h2>
                  </div>
                </div>

                {/* Modal Content - Scrollable */}
                <div className="overflow-y-auto max-h-[calc(90vh-16rem)] p-6">
                  {/* Description */}
                  <p className="text-gray-300 text-lg mb-6 leading-relaxed">
                    {selectedBundle.description}
                  </p>

                  {/* Pricing & Savings */}
                  <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-8">
                    <div className="bg-slate-800/60 rounded-xl p-4 border border-purple-500/30">
                      <div className="flex items-center gap-2 mb-2">
                        <TrendingUp className="w-5 h-5 text-green-400" />
                        <span className="text-gray-400 text-sm">Your Savings</span>
                      </div>
                      <p className="text-2xl font-bold text-green-400">
                        {selectedBundle.savings_percentage?.toFixed(0) || '0'}% OFF
                      </p>
                    </div>

                    <div className="bg-slate-800/60 rounded-xl p-4 border border-purple-500/30">
                      <span className="text-gray-400 text-sm block mb-2">Original Price</span>
                      <p className="text-2xl font-bold text-gray-500 line-through">
                        ${selectedBundle.full_price.toFixed(2)}
                      </p>
                    </div>

                    <div className="bg-gradient-to-br from-purple-600 to-pink-600 rounded-xl p-4">
                      <span className="text-white/80 text-sm block mb-2">Bundle Price</span>
                      <p className="text-3xl font-bold text-white">
                        ${(selectedBundle.discount_price || selectedBundle.full_price).toFixed(2)}
                      </p>
                    </div>
                  </div>

                  {/* Courses in Bundle */}
                  <div className="mb-4">
                    <h3 className="text-2xl font-bold text-white mb-4 flex items-center gap-2">
                      <BookOpen className="w-6 h-6 text-purple-400" />
                      Courses in this Bundle ({bundleCourses.length})
                    </h3>
                  </div>

                  <div className="space-y-4">
                    {bundleCourses.map((course, index) => (
                      <div
                        key={course.id}
                        className="bg-slate-800/60 border border-purple-500/30 rounded-xl p-4 hover:border-purple-500 transition-all duration-300 hover:shadow-lg hover:shadow-purple-500/20 cursor-pointer group"
                        onClick={() => navigate(`/classroom/course/${course.id}`)}
                      >
                        <div className="flex gap-4">
                          {/* Course Thumbnail */}
                          <div className="flex-shrink-0 w-24 h-24 rounded-lg overflow-hidden bg-gradient-to-br from-indigo-600 to-purple-600">
                            {course.thumbnail_url ? (
                              <img
                                src={course.thumbnail_url}
                                alt={course.title}
                                className="w-full h-full object-cover"
                              />
                            ) : (
                              <div className="w-full h-full flex items-center justify-center">
                                <BookOpen className="w-10 h-10 text-white/50" />
                              </div>
                            )}
                          </div>

                          {/* Course Info */}
                          <div className="flex-grow">
                            <div className="flex items-start justify-between">
                              <div>
                                <div className="flex items-center gap-2 mb-1">
                                  <span className="text-purple-400 text-xs font-semibold">
                                    Course {index + 1}
                                  </span>
                                  {course.skill_level && (
                                    <span className="text-gray-500 text-xs">
                                      • {course.skill_level}
                                    </span>
                                  )}
                                </div>
                                <h4 className="text-lg font-bold text-white mb-2 group-hover:text-purple-400 transition-colors">
                                  {course.title}
                                </h4>
                                <p className="text-gray-400 text-sm line-clamp-2 mb-2">
                                  {course.description}
                                </p>
                                <div className="flex items-center gap-4 text-xs text-gray-500">
                                  {course.duration_hours && (
                                    <span className="flex items-center gap-1">
                                      <Clock className="w-3 h-3" />
                                      {course.duration_hours} hours
                                    </span>
                                  )}
                                  <span className="px-2 py-1 bg-purple-500/20 text-purple-400 rounded">
                                    {course.category}
                                  </span>
                                </div>
                              </div>
                            </div>
                          </div>
                        </div>
                      </div>
                    ))}
                  </div>

                  {/* CTA Button */}
                  <div className="mt-8 flex justify-center">
                    <button
                      onClick={() => setShowModal(false)}
                      className="px-8 py-3 bg-gradient-to-r from-purple-600 to-pink-600 text-white font-semibold rounded-lg hover:shadow-lg hover:shadow-purple-500/50 transition-all"
                    >
                      Close
                    </button>
                  </div>
                </div>
              </>
            ) : null}
          </div>
        </div>
      )}
    </div>
  );
};

export default DashboardBundles;
